import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router'
import { api, query } from '../api/client'
import { filterParams, filtersFrom, type CrashGroupDetail } from '../api/crashes'
import { CountList } from '../components/admin/crashes/CountList'
import { StackTable } from '../components/admin/crashes/StackTable'
import { useCrashAction } from '../components/admin/crashes/useCrashAction'
import { formatDateTime } from '../lib/format'

/** One stack group under the filters it was listed with: its stack, breakdowns and reports. */
export function CrashGroupScreen() {
	const { signature = '' } = useParams()
	const [params] = useSearchParams()
	const navigate = useNavigate()
	const filters = filtersFrom(params)
	const [page, setPage] = useState(1)
	const [selected, setSelected] = useState<Set<string>>(new Set())
	const group = useQuery({
		queryKey: ['admin-crash-group', signature, params.toString(), page],
		queryFn: () =>
			api.get<CrashGroupDetail>(
				`/api/v1/admin/crashes/groups/${signature}${query({ ...filterParams(filters), page })}`,
			),
	})
	const resolve = useCrashAction((ids: string[]) =>
		api.post('/api/v1/admin/crashes/resolve', { ids, reopen: filters.resolved }),
	)
	const remove = useCrashAction(() =>
		api.delete(`/api/v1/admin/crashes/groups/${signature}${query(filterParams(filters))}`),
	)
	const toggle = (id: string) =>
		setSelected((s) => {
			const next = new Set(s)
			if (next.has(id)) next.delete(id)
			else next.add(id)
			return next
		})
	const data = group.data

	return (
		<div className="stack">
			<div className="page-header">
				<div>
					<Link to="/admin?tab=crashes" className="small">
						← Crashes and Errors
					</Link>
					<h1 style={{ margin: '0.2rem 0 0', fontFamily: 'var(--mono)' }}>{data?.title ?? signature}</h1>
					<div className="muted small mono">
						{signature} · last {filters.days} days{filters.resolved ? ' · resolved' : ''}
					</div>
				</div>
			</div>
			{group.isError && <p className="error">{group.error.message}</p>}
			{data && (
				<>
					<section className="card stack">
						<h2 style={{ margin: 0 }}>{data.stack.heading}</h2>
						<p className="muted small" style={{ margin: 0 }}>
							{data.stack.symbolicated
								? 'The latest fully symbolicated report in this window.'
								: 'No fully symbolicated report in this window; showing the latest report.'}
						</p>
						<StackTable stack={data.stack} />
					</section>
					<section className="card row" style={{ alignItems: 'flex-start', gap: '2rem', flexWrap: 'wrap' }}>
						<CountList title="Model" counts={data.byModel} />
						<CountList title="OS" counts={data.byOs} />
						<CountList title="Build" counts={data.byBuild} />
					</section>
					<section className="card stack">
						<div className="row" style={{ justifyContent: 'space-between' }}>
							<h2 style={{ margin: 0 }}>Reports</h2>
							<div className="row">
								<button
									type="button"
									disabled={!selected.size || resolve.isPending}
									onClick={() => resolve.mutate([...selected], { onSuccess: () => setSelected(new Set()) })}
								>
									{filters.resolved ? 'Reopen selected' : 'Mark selected resolved'}
								</button>
								<button
									type="button"
									className="danger"
									onClick={() =>
										window.confirm('Delete every report in this group for this window? There is no undo.') &&
										remove.mutate(undefined, { onSuccess: () => navigate('/admin?tab=crashes') })
									}
								>
									Delete group
								</button>
							</div>
						</div>
						<div className="table-scroll">
							<table>
								<thead>
									<tr>
										<th />
										<th>Report</th>
										<th>Occurred</th>
										<th>Build</th>
										<th>OS</th>
										<th>Model</th>
										<th>Symbols</th>
									</tr>
								</thead>
								<tbody>
									{data.reports.map((r) => (
										<tr key={r.id}>
											<td>
												<input
													type="checkbox"
													aria-label={`Select report ${r.id}`}
													checked={selected.has(r.id)}
													onChange={() => toggle(r.id)}
												/>
											</td>
											<td>
												<Link to={`/admin/crashes/${r.id}`}>{r.kind}</Link>
											</td>
											<td className="small">{formatDateTime(r.occurredAt)}</td>
											<td>{r.appBuild ?? 'Unknown'}</td>
											<td>{r.osVersion ?? 'Unknown'}</td>
											<td>{r.model ?? 'Unknown'}</td>
											<td className="small">{r.symbolicationState}</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
						<div className="row">
							{page > 1 && (
								<button type="button" onClick={() => setPage(page - 1)}>
									Previous
								</button>
							)}
							{data.hasMore && (
								<button type="button" onClick={() => setPage(page + 1)}>
									More
								</button>
							)}
						</div>
					</section>
				</>
			)}
		</div>
	)
}
